import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  Search,
  Book,
  MessageSquare,
  FileText,
  Users,
  Download,
  Plus,
  Calendar,
  Shield,
  Zap,
  AlertTriangle,
  Flame,
  Droplet,
  Settings,
  Box,
  Lock,
  Loader2,
  CheckCircle,
  ChevronRight,
  BarChart3
} from 'lucide-react';
import axios from 'axios';
import SafetyStatistics from './SafetyStatistics';
import ToolboxTalksManager from './ToolboxTalksManager';
import SafetyMeetingsManager from './SafetyMeetingsManager';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  ppe: Shield,
  fall_protection: AlertTriangle,
  electrical: Zap,
  fire_safety: Flame,
  chemical: Droplet,
  machinery: Settings,
  confined_spaces: Box,
  lockout_tagout: Lock,
  general: Book
};

function KnowledgeCenterPage() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.defaultTab || 'search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [aiQuery, setAiQuery] = useState('');
  const [aiAnswer, setAiAnswer] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [toolboxTalks, setToolboxTalks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Toolbox Talk Form State
  const [toolboxForm, setToolboxForm] = useState({
    topic: '',
    industry: '',
    duration_minutes: 10,
    audience: 'general',
    specific_hazards: '',
    include_discussion_questions: true,
    include_signin_sheet: true
  });

  // Meeting Agenda Form State
  const [meetingForm, setMeetingForm] = useState({
    meeting_date: new Date().toISOString().split('T')[0],
    duration_minutes: 30,
    include_recent_incidents: true,
    include_seasonal_hazards: true,
    custom_topics: ''
  });

  const getToken = () => localStorage.getItem('auth_token');
  const getCompanyId = () => {
    const user = JSON.parse(localStorage.getItem('user_data') || '{}');
    return user.company_id || 'default-company';
  };

  useEffect(() => {
    fetchCategories();
    fetchToolboxTalks();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/knowledge/categories`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchToolboxTalks = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/toolbox-talks`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setToolboxTalks(response.data || []);
    } catch (error) {
      console.error('Error fetching toolbox talks:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/knowledge/search?q=${encodeURIComponent(searchQuery)}${selectedCategory ? `&category=${selectedCategory}` : ''}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAIQuery = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    setLoading(true);
    setAiAnswer(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/knowledge/query`,
        { query: aiQuery, category: selectedCategory },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setAiAnswer(response.data);
    } catch (error) {
      console.error('Error querying AI:', error);
      setAiAnswer({ error: error.response?.data?.detail || error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateToolboxTalk = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...toolboxForm,
        specific_hazards: toolboxForm.specific_hazards ? toolboxForm.specific_hazards.split(',').map(s => s.trim()) : null
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/knowledge/generate-toolbox-talk`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      alert('Toolbox talk generated successfully!');
      fetchToolboxTalks();
      // Reset form
      setToolboxForm({
        topic: '',
        industry: '',
        duration_minutes: 10,
        audience: 'general',
        specific_hazards: '',
        include_discussion_questions: true,
        include_signin_sheet: true
      });
    } catch (error) {
      console.error('Error generating toolbox talk:', error);
      alert('Failed to generate toolbox talk: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMeetingAgenda = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        company_id: getCompanyId(),
        ...meetingForm,
        custom_topics: meetingForm.custom_topics ? meetingForm.custom_topics.split(',').map(s => s.trim()) : null
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/knowledge/generate-meeting-agenda`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      alert('Meeting agenda generated successfully! Check your email or safety meetings section.');
    } catch (error) {
      console.error('Error generating meeting agenda:', error);
      alert('Failed to generate meeting agenda: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900">Knowledge Center</h1>
        <p className="text-base text-gray-600 mt-2">
          OSHA standards, toolbox talks, and safety resources
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="search" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            <span className="hidden sm:inline">Search & Browse</span>
          </TabsTrigger>
          <TabsTrigger value="ai-qa" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">AI Q&A</span>
          </TabsTrigger>
          <TabsTrigger value="toolbox-talks" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Toolbox Talks</span>
          </TabsTrigger>
          <TabsTrigger value="meetings" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Meetings</span>
          </TabsTrigger>
          <TabsTrigger value="statistics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Safety Stats</span>
          </TabsTrigger>
        </TabsList>

        {/* Search & Browse Tab */}
        <TabsContent value="search">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Categories</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant={selectedCategory === null ? "default" : "outline"}
                    className="w-full justify-start"
                    onClick={() => setSelectedCategory(null)}
                  >
                    <Book className="h-4 w-4 mr-2" />
                    All Categories
                  </Button>
                  {categories.map((cat) => {
                    const Icon = CATEGORY_ICONS[cat.id] || Book;
                    return (
                      <Button
                        key={cat.id}
                        variant={selectedCategory === cat.id ? "default" : "outline"}
                        className="w-full justify-start"
                        onClick={() => setSelectedCategory(cat.id)}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {cat.name}
                      </Button>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Search Knowledge Base</CardTitle>
                  <CardDescription>
                    Search OSHA standards, guidelines, and best practices
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <Input
                      placeholder="Search for safety topics, standards, procedures..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1"
                    />
                    <Button type="submit" disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">
                    {searchResults.length} Results Found
                  </h3>
                  {searchResults.map((result, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-base">{result.title}</CardTitle>
                            {result.osha_reference && (
                              <Badge variant="outline" className="mt-1">
                                {result.osha_reference}
                              </Badge>
                            )}
                          </div>
                          {result.is_custom && (
                            <Badge className="bg-blue-100 text-blue-800">Custom</Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-700">{result.content}</p>
                        {result.keywords && result.keywords.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {result.keywords.map((kw, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {kw}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* AI Q&A Tab */}
        <TabsContent value="ai-qa">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                AI-Powered Safety Expert
              </CardTitle>
              <CardDescription>
                Ask questions about OSHA standards, safety procedures, and best practices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleAIQuery} className="space-y-4">
                <div>
                  <Label htmlFor="ai-query">Your Question</Label>
                  <Textarea
                    id="ai-query"
                    placeholder="e.g., What are the requirements for fall protection at heights above 6 feet?"
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    rows={3}
                    required
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Getting Answer...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Ask AI Expert
                    </>
                  )}
                </Button>
              </form>

              {aiAnswer && !aiAnswer.error && (
                <Card className="border-green-200 bg-green-50">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Answer
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">
                      {aiAnswer.answer}
                    </div>
                    {aiAnswer.sources && aiAnswer.sources.length > 0 && (
                      <div className="pt-3 border-t">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Sources:</p>
                        <div className="flex flex-wrap gap-1">
                          {aiAnswer.sources.map((source, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {source}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {aiAnswer && aiAnswer.error && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-red-600">{aiAnswer.error}</p>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Toolbox Talks Tab */}
        <TabsContent value="toolbox-talks">
          <ToolboxTalksManager />
        </TabsContent>

        {/* Safety Meetings Tab */}
        <TabsContent value="meetings">
          <SafetyMeetingsManager />
        </TabsContent>

        {/* Safety Statistics Tab */}
        <TabsContent value="statistics">
          <SafetyStatistics />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default KnowledgeCenterPage;
